import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import { AWAssetDto, AWStageDto } from '@api/Api';
import { CheckTaskIcon } from '@icons';

type ReviewMediaCellProps = {
  stages: AWStageDto[];
  assets: AWAssetDto[];
  currentStageId?: string | null;
  isFinished: boolean;
  isPaused: boolean;
};

export default memo(function ReviewMediaCell({
  stages,
  assets,
  currentStageId,
  isPaused,
  isFinished
}: ReviewMediaCellProps) {
  const currentStage = useMemo(() => {
    if (!stages?.length) return null;
    return stages.find((stage) => stage.id === currentStageId) || stages[0];
  }, [stages, currentStageId]);

  const currentMember = useMemo(
    () => currentStage?.members.find((member) => member.me),
    [currentStage?.members]
  );

  const toReviewCount = useMemo(() => {
    if (!currentMember?.canSubmitDecision) {
      return 0;
    }
    return assets.length;
  }, [assets.length, currentMember?.canSubmitDecision]);

  const reviewCount = useMemo(() => {
    if (!currentMember?.canSubmitDecision) {
      return 0;
    }
    return assets.filter((asset) =>
      currentMember?.decisions?.some(
        (x) => x.assetVersionId === asset.versionId && x.decision
      )
    ).length;
  }, [assets, currentMember?.canSubmitDecision, currentMember?.decisions]);

  const canReview = toReviewCount > 0;
  const isReviewed = canReview && reviewCount === toReviewCount;

  return (
    <div
      className={classNames('review-media-cell', {
        'b-can-review': canReview,
        'b-reviewed': isReviewed,
        'b-finished': isFinished,
        'b-paused': isPaused
      })}
    >
      <span className="review-media-cell-icon">
        <CheckTaskIcon />
      </span>
      {canReview && (
        <>
          {reviewCount} / {toReviewCount}
        </>
      )}
    </div>
  );
});
